import { seemsLikeObjectId } from 'z3k-parser/lib/id';
import { isFlagSet, setFlag } from 'z3k-parser/lib/flags';
import { removeNulls } from 'utils';
import { arrayGetSet, createAccessors } from 'z3k-parser/lib/accessories';
import { HeatingCircuit } from '../16-heating-circuit';
export var HeatingMode;
(function (HeatingMode) {
    let Flags;
    (function (Flags) {
        Flags[Flags["MANUAL_COLOR"] = 1] = "MANUAL_COLOR";
        Flags[Flags["COLOR_BIT_1"] = 2] = "COLOR_BIT_1";
        Flags[Flags["COLOR_BIT_2"] = 4] = "COLOR_BIT_2";
        Flags[Flags["COLOR_BIT_3"] = 8] = "COLOR_BIT_3";
        Flags[Flags["COLOR_BIT_4"] = 16] = "COLOR_BIT_4";
        Flags[Flags["IS_HIDDEN"] = 32] = "IS_HIDDEN";
    })(Flags = HeatingMode.Flags || (HeatingMode.Flags = {}));
    let StatusFlags;
    (function (StatusFlags) {
        StatusFlags[StatusFlags["IS_MODE_WITH_RESERVE"] = 1] = "IS_MODE_WITH_RESERVE";
        StatusFlags[StatusFlags["IS_RESERVE_TURNED_ON"] = 2] = "IS_RESERVE_TURNED_ON";
        StatusFlags[StatusFlags["IS_BOILER_MODE_PATCHED"] = 4] = "IS_BOILER_MODE_PATCHED";
    })(StatusFlags = HeatingMode.StatusFlags || (HeatingMode.StatusFlags = {}));
    HeatingMode.OFF_MODE = 0;
    HeatingMode.REQUEST_MODE = 1;
    HeatingMode.ALWAYS_ON_MODE = 2;
    HeatingMode.RESERVE_MODE = 255;
    HeatingMode.special_modes = [HeatingMode.OFF_MODE, HeatingMode.REQUEST_MODE, HeatingMode.ALWAYS_ON_MODE, HeatingMode.RESERVE_MODE];
    function getSpecialModeName(temp) {
        switch (temp) {
            case HeatingMode.OFF_MODE:
                return 'Выкл';
            case HeatingMode.REQUEST_MODE:
                return 'По запросу';
            case HeatingMode.ALWAYS_ON_MODE:
                return 'Всегда';
            case HeatingMode.RESERVE_MODE:
                return 'Резерв';
            default:
                return '';
        }
    }
    HeatingMode.getSpecialModeName = getSpecialModeName;
    function getTargetTemperature(mode, circuit_id) {
        const temperature_setting = mode.heating_zones.find(({ heating_circuit }) => 
        // prettier-ignore
        heating_circuit === circuit_id)?.temperature_setting;
        if (temperature_setting == null || temperature_setting === HeatingMode.OFF_MODE) {
            return null;
        }
        if (!seemsLikeObjectId(temperature_setting)) {
            return (temperature_setting - 2730) / 10;
        }
        return null;
    }
    HeatingMode.getTargetTemperature = getTargetTemperature;
    function makeTemperatureSetting(target_temp_C) {
        if (target_temp_C == null) {
            return HeatingMode.OFF_MODE;
        }
        return ~~(target_temp_C * 10 + 2730);
    }
    HeatingMode.makeTemperatureSetting = makeTemperatureSetting;
    function isHidden(mode) {
        return isFlagSet(mode, Flags.IS_HIDDEN);
    }
    HeatingMode.isHidden = isHidden;
    function changeHidden(mode, enabled) {
        return setFlag(mode, Flags.IS_HIDDEN, enabled);
    }
    HeatingMode.changeHidden = changeHidden;
    function hasManualColor(mode) {
        return isFlagSet(mode, Flags.MANUAL_COLOR);
    }
    HeatingMode.hasManualColor = hasManualColor;
    function getManualColor(mode) {
        return (mode.setting_register >> 1) & 0x0f;
    }
    HeatingMode.getManualColor = getManualColor;
    function getColor(mode) {
        return mode.setting_register & 0x1f;
    }
    HeatingMode.getColor = getColor;
    function setColor(mode, color) {
        return { ...mode, setting_register: (mode.setting_register & ~0x1f) | color };
    }
    HeatingMode.setColor = setColor;
    function getAutoColor(mode_index) {
        return mode_index % 10;
    }
    HeatingMode.getAutoColor = getAutoColor;
    let MODE_TYPE;
    (function (MODE_TYPE) {
        MODE_TYPE["BOILER"] = "BOILER";
        MODE_TYPE["CONSUMER"] = "CONSUMER";
        MODE_TYPE["MIXED"] = "MIXED";
    })(MODE_TYPE = HeatingMode.MODE_TYPE || (HeatingMode.MODE_TYPE = {}));
    function getModeTypePerZone(heating_mode, heating_circuits, boiler_cascades) {
        const zones_heating_circuit_ids = removeNulls((heating_mode.heating_zones || []).map((zone) => zone.heating_circuit));
        //Посмотрим, кто используется в hc
        const used_hc = Object.fromEntries(removeNulls((heating_circuits || []).map((hc) => zones_heating_circuit_ids.includes(hc.id)
            ? HeatingCircuit.isBoiler(hc)
                ? [hc.id, MODE_TYPE.BOILER]
                : [hc.id, MODE_TYPE.CONSUMER]
            : null)));
        const used_cascades = Object.fromEntries(removeNulls((boiler_cascades || []).map(({ id }) => zones_heating_circuit_ids.includes(id)
            ? [id, MODE_TYPE.BOILER]
            : null)));
        return {
            ...used_hc,
            ...used_cascades,
        };
    }
    HeatingMode.getModeTypePerZone = getModeTypePerZone;
    function getModeType(heating_mode, heating_circuits, boiler_cascades) {
        const all_use = new Set(Object.values(getModeTypePerZone(heating_mode, heating_circuits, boiler_cascades)));
        //Значит создали новый контур, который нигде не участвует
        if (all_use.size === 0) {
            return MODE_TYPE.CONSUMER;
        }
        return all_use.size > 1 ? MODE_TYPE.MIXED : all_use.values().next().value;
    }
    HeatingMode.getModeType = getModeType;
    function hasBoilerMode(heating_mode, heating_circuits, boiler_cascades) {
        return [MODE_TYPE.MIXED, MODE_TYPE.BOILER].includes(getModeType(heating_mode, heating_circuits, boiler_cascades));
    }
    HeatingMode.hasBoilerMode = hasBoilerMode;
    function hasConsumerMode(heating_mode, heating_circuits, boiler_cascades) {
        return [MODE_TYPE.CONSUMER].includes(getModeType(heating_mode, heating_circuits, boiler_cascades));
    }
    HeatingMode.hasConsumerMode = hasConsumerMode;
    function filterBoilerMode(heating_mode, heating_circuits, boiler_cascades) {
        return (heating_mode.force_boiler_mode ||
            hasBoilerMode(heating_mode, heating_circuits, boiler_cascades));
    }
    HeatingMode.filterBoilerMode = filterBoilerMode;
    function filterConsumerMode(heating_mode, heating_circuits, boiler_cascades) {
        return (!heating_mode.force_boiler_mode &&
            hasConsumerMode(heating_mode, heating_circuits, boiler_cascades));
    }
    HeatingMode.filterConsumerMode = filterConsumerMode;
    function isOffHz(hz) {
        return hz.temperature_setting === 0;
    }
    HeatingMode.isOffHz = isOffHz;
    function isOffMode(hm) {
        return hm.heating_zones.every(HeatingMode.isOffHz);
    }
    HeatingMode.isOffMode = isOffMode;
    HeatingMode.get = createAccessors(arrayGetSet('heating_modes'), {
        name: 'Режим отопления',
        heating_zones: [],
        setting_register: 0,
        water_sensor: null,
        start_reserve_delay: 10,
        force_boiler_mode: false,
        max_modulation_level: 0,
    });
})(HeatingMode || (HeatingMode = {}));
